import { ChangeEvent, useEffect, useState } from 'react';
import { FormControl, OutlinedInput, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { debounce } from '@mui/material/utils';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { Lock } from '@mui/icons-material';
import { Colors } from '../styles/colors';

type SearchFieldType = {
  initialValue?: string;
  disabled?: boolean;
  debounceTime?: number;
  onSearch: (searchString: string) => void;
};

export default function SearchField(props: SearchFieldType) {
  const { initialValue = '', disabled = false, debounceTime = 500, onSearch } = props;
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue || '');
  const [isUserInput, setIsUserInput] = useState<boolean>(false);

  const doSearch = (val) => {
    setLoading(false);
    setIsUserInput(false);
    if (val.length >= 2) onSearch(val);
  };

  const debouncedSearch = debounce(doSearch, debounceTime);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setIsUserInput(true);
    if (event.target.value.length >= 2) setLoading(true);
    else {
      setLoading(false);
      onSearch('');
    }
  };

  useEffect(() => {
    if (isUserInput) {
      debouncedSearch(value);
    }
    return () => {
      debouncedSearch.clear();
    };
  }, [value, debouncedSearch, isUserInput]);

  const handleClearValue = () => {
    setValue('');
    onSearch('');
    setIsUserInput(false);
  };

  let endAdornment: React.ReactNode;
  if (loading) {
    endAdornment = <CircularProgress size={20} />;
  } else if (disabled) {
    endAdornment = <Lock sx={{ color: 'text.disabled' }} />;
  } else {
    endAdornment = (
      <IconButton aria-label='reset search' onClick={handleClearValue} edge='end' size='small' sx={{ visibility: value ? 'visible' : 'hidden' }}>
        <ClearIcon />
      </IconButton>
    );
  }

  return (
    <FormControl
      variant='outlined'
      size='small'
      sx={{
        '& .MuiInputBase-root': {
          backgroundColor: disabled ? Colors.Form.DisabledInput : '#fff',
        },
        '& .MuiInputAdornment-root': {
          fontSize: '1rem',
          color: 'grey.400',
        },
      }}
      disabled={disabled}
    >
      <OutlinedInput
        id='outlined-adornment-password'
        type='text'
        value={value}
        placeholder={t('common:button.search')}
        onChange={handleChange}
        startAdornment={
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position='end' sx={{ width: 5, justifyContent: 'flex-end' }}>
            {endAdornment}
          </InputAdornment>
        }
      />
    </FormControl>
  );
}
